// Locale → copy for the public "How preview-first uploads work" page, following the site's language
// like the pricing pages (src/content/pricing): English (source of truth), Spanish,
// Portuguese, German, French, Italian, Dutch, Norwegian (Bokmål), Japanese, Simplified Chinese, Malay and Thai
// (SEO_LANGS in src/lib/seo/site.ts); any other language falls back to English. A ?lang= the site UI doesn't
// have (fr, nl, no, th) still renders this copy; only the nav and footer stay in English (i18next fallbackLng).
import { useTranslation } from "react-i18next";
import { getApiBaseDomain } from "@/lib/apiDomain";
import { baseLang } from "@/lib/seo/site";
import en, { type PreviewFirstCopy } from "./copy.en";
import es from "./copy.es";
import pt from "./copy.pt";
import de from "./copy.de";
import fr from "./copy.fr";
import it from "./copy.it";
import nl from "./copy.nl";
import no from "./copy.no";
import ja from "./copy.ja";
import zh from "./copy.zh";
import ms from "./copy.ms";
import th from "./copy.th";

/** Where the page lives on the marketing site (linked from the web uploader, so don't change it). */
export const PREVIEW_FIRST_PATH = "/preview-first-uploads";

/** The page's absolute URL for the current environment (photohawk.com or photohawk-staging.com), for
 *  links from the uploader, which also runs on upload.<domain> and on tenant hosts. */
export const previewFirstPageUrl = () => `https://${getApiBaseDomain()}${PREVIEW_FIRST_PATH}`;

const COPY_BY_LANG: Record<string, PreviewFirstCopy> = { en, es, pt, de, fr, it, nl, no, ja, zh, ms, th };

export const getPreviewFirstCopy = (lang: string | undefined): PreviewFirstCopy => COPY_BY_LANG[baseLang(lang)] ?? en;

export function usePreviewFirstCopy(): { copy: PreviewFirstCopy; lang: string } {
  const { i18n } = useTranslation();
  const base = baseLang(i18n.language);
  const lang = COPY_BY_LANG[base] ? base : "en";
  return { copy: COPY_BY_LANG[lang], lang };
}

export type { PreviewFirstCopy };
